import { getFormDefinitionById, listFormDefinitions, listFormSubmissions } from '../../../util/APIUtils';
import store, { showToastWithType } from '../../core/store';
import { GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS } from '../constants/form-api.constants';
import { FORM_SUBMISSIONS_ACTION_LABELS } from '../constants/form-submission.constants';
import { buildEditAction, buildPreviewFormAction, buildToggleAction } from './form.util';
import { collectSubmissionFieldMetaFromDefinition } from './form-submission-schema.util';

const SUBMISSIONS_TITLE_SUFFIX = 'submissions';

const endsWithSubmissionsSuffix = (title) =>
  title.length > SUBMISSIONS_TITLE_SUFFIX.length &&
  title.slice(-SUBMISSIONS_TITLE_SUFFIX.length).toLowerCase() === SUBMISSIONS_TITLE_SUFFIX;

const stripTrailingSubmissionsWord = (title) => {
  if (!endsWithSubmissionsSuffix(title)) {
    return title;
  }
  const beforeSuffix = title.slice(0, -SUBMISSIONS_TITLE_SUFFIX.length);
  const lastBefore = beforeSuffix.charAt(beforeSuffix.length - 1);
  if (!/\s/.test(lastBefore)) {
    return title;
  }
  return beforeSuffix.trimEnd();
};

/** Subtitle already reads "Form Submissions"; strip redundant trailing word from API form name. */
export const pageHeadingFromFormTitle = (formTitle) => {
  if (formTitle == null || formTitle === '') {
    return 'Submissions';
  }
  return stripTrailingSubmissionsWord(String(formTitle).trimEnd());
};

export const normalizeSubmissionsResponse = (raw) => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && Array.isArray(raw.data)) {
    return raw.data;
  }
  return [];
};

export const showFormSubmissionErrorToast = (message) => {
  store.dispatch(showToastWithType(message, 'error'));
};

const findFormDefinitionInList = (rows, formId) => {
  if (!Array.isArray(rows)) {
    return null;
  }
  return rows.find((f) => String(f.id) === String(formId)) ?? null;
};

export const loadFormDefinitionWithFallback = async (formId) => {
  try {
    return await getFormDefinitionById(formId, GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS);
  } catch (e) {
    if (e?.message !== 'INACTIVE_FORM') {
      throw e;
    }
    const listRes = await listFormDefinitions();
    const raw = listRes?.data ?? listRes;
    const rows = Array.isArray(raw) ? raw : raw?.content;
    return findFormDefinitionInList(rows, formId);
  }
};

/**
 * Loads submissions and form definition for the submissions page.
 * @param {string | number} formId
 */
export const fetchFormSubmissionsPageData = async (formId) => {
  const submissionPayload = await listFormSubmissions(formId);
  const definition = await loadFormDefinitionWithFallback(formId);
  return {
    formTitle: String(definition?.name ?? ''),
    formDefinition: definition ?? null,
    submissions: normalizeSubmissionsResponse(submissionPayload),
  };
};

/**
 * @param {{
 *   submissions: readonly object[],
 *   formDefinition: unknown,
 *   formTitle: string,
 * }} input
 */
export const buildFormSubmissionsExportInput = ({ submissions, formDefinition, formTitle }) => ({
  submissions,
  formTitle,
  ...collectSubmissionFieldMetaFromDefinition(formDefinition),
});

const mapFormActionToDropdownOption = (action) => ({
  label: action.title,
  value: action.id,
  onClick: action.onClick,
  icon: action.icon,
});

/**
 * DataGrid primary-button dropdown options for form actions on the submissions page.
 * @param {object | null} form — mapped form row (`mapApiFormDefinitionToForm`)
 * Handlers are omitted when the user lacks the permission behind them, so each one is optional.
 * @param {{
 *   onPreview?: (form: object) => void,
 *   onEdit?: (form: object) => void,
 *   onToggleActive?: (form: object) => void,
 * }} handlers
 */
export const buildFormSubmissionsActionsDropdownOptions = (form, handlers) => {
  if (form == null) {
    return [];
  }
  const items = [];
  if (typeof handlers.onPreview === 'function') {
    items.push(buildPreviewFormAction(form, handlers.onPreview, FORM_SUBMISSIONS_ACTION_LABELS));
  }
  if (typeof handlers.onEdit === 'function') {
    items.push(buildEditAction(form, handlers.onEdit, FORM_SUBMISSIONS_ACTION_LABELS));
  }
  const toggle = buildToggleAction(form, handlers.onToggleActive, FORM_SUBMISSIONS_ACTION_LABELS);
  if (toggle) {
    items.push(toggle);
  }
  return items.map(mapFormActionToDropdownOption);
};
